import axios, { AxiosInstance } from 'axios';
import APP_UTILITIES from '@/utilities/commonFunctions';
import { Vue } from 'vue-property-decorator';
import { uuid } from 'vue-uuid';
import UIkit from 'uikit';
import { getInstance } from '@/auth';
import jwt_decode from 'jwt-decode';
import { Auth0Token } from '@/Model/sessions/types';

const BASE_URLS = {
  VUE_APP_BASE_URL: process.env.VUE_APP_BASE_URL,
  VUE_APP_ADMIN_URL: process.env.VUE_APP_ADMIN_URL,
  VUE_APP_BASE_IDENTITY_URL: process.env.VUE_APP_BASE_IDENTITY_URL,
  VUE_APP_BASE_ENROLLMENT_URL: process.env.VUE_APP_BASE_ENROLLMENT_URL,
  VUE_APP_BASE_LMS_URL: process.env.VUE_APP_BASE_LMS_URL,
};

axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.post['Content-Type'] = 'application/json; charset=UTF-8';

export const getDecodedToken = async (): Promise<Auth0Token | undefined> => {
  const authService = getInstance();
  if (authService.isAuthenticated) {
    const token: string = await authService.getTokenSilently();
    if (token) {
      const decodedToken: any = jwt_decode(token);
      return decodedToken;
    }
  }
};

function createRequestInterceptor(axiosInstance: AxiosInstance) {
  try {
    axiosInstance.interceptors.request.use(async (config) => {
      const authService = getInstance();
      const token = await authService.getTokenSilently();
      token && (config.headers.Authorization = `Bearer ${token}`);
      return config;
    });
  }
  catch (error) {
    console.error(`Error trying to get the token (${error})`);
  }
}

const httpClient = () => {
  const httpClientInstance = axios.create({
    baseURL: BASE_URLS.VUE_APP_BASE_URL,
    headers: {
      CorrelationId: uuid.v1(),
    },
  });

  function createAxiosResponseInterceptor(axiosInstance: any) {
    const interceptor = axiosInstance.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        if (error.response.status !== 401) {
          Vue.prototype.$eventHub.$emit('showErrorPopup', error.response.data);
          return Promise.reject(error.response);
        }
        else if (error.response.status === 401) {
          APP_UTILITIES.removeCookies();
          window.location.href =
            location.href &&
            location.href.split('#')[0] + '#/login?type=logout';
        }
        axiosInstance.interceptors.response.eject(interceptor);
      }
    );
  }

  createRequestInterceptor(httpClientInstance);
  createAxiosResponseInterceptor(httpClientInstance);
  return httpClientInstance;
};

const httpAdmin = ({ interceptor }: { interceptor?: boolean } = {}) => {
  const httpAdminInstance = axios.create({
    baseURL: BASE_URLS.VUE_APP_ADMIN_URL,
  });

  function createAxiosResponseInterceptor(axiosInstance: any) {
    const interceptor = axiosInstance.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        if (error && error.response && error.response.status !== 401) {
          Vue.prototype.$eventHub.$emit('showErrorPopup', error.response.data);
          return Promise.reject(error.response);
        }
        else if (error && error.response && error.response.status === 401) {
          APP_UTILITIES.removeCookies();
          window.location.href =
            location.href &&
            location.href.split('#')[0] + '#/login?type=logout';
        }
        axiosInstance.interceptors.response.eject(interceptor);
      }
    );
  }
  interceptor && createRequestInterceptor(httpAdminInstance);
  createAxiosResponseInterceptor(httpAdminInstance);
  return httpAdminInstance;
};

const httpIdentityClient = ({
  interceptor = true,
}: {
  interceptor: boolean;
}) => {
  const httpIdentityInstance = axios.create({
    baseURL: BASE_URLS.VUE_APP_BASE_IDENTITY_URL,
    headers: {
      'Access-Control-Allow-Origin': '*',
    },
  });

  function createAxiosResponseInterceptor(axiosInstance: any) {
    const interceptor = axiosInstance.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        if (error.response.status !== 401 && error.response.status !== 500) {
          Vue.prototype.$eventHub.$emit('showErrorPopup', error.response.data);
          return Promise.reject(error.response);
        }
        else if (error.response.status === 401) {
          APP_UTILITIES.removeCookies();
          window.location.href =
            location.href &&
            location.href.split('#')[0] + '#/login?type=logout';
        }
        else if (error.response.status === 500) {
          const data = {
            name: 'Common_Error',
          };
          Vue.prototype.$eventHub.$emit('showErrorPopup', data);
        }
        axiosInstance.interceptors.response.eject(interceptor);
      }
    );
  }
  interceptor && createRequestInterceptor(httpIdentityInstance);
  createAxiosResponseInterceptor(httpIdentityInstance);
  return httpIdentityInstance;
};

const httpLMSClient = () => {
  const httpLMSInstance = axios.create({
    baseURL: BASE_URLS.VUE_APP_BASE_LMS_URL,
  });

  function createAxiosResponseInterceptor(axiosInstance: any) {
    const interceptor = axiosInstance.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        if (error.response.status !== 401) {
          Vue.prototype.$eventHub.$emit('showErrorPopup', error.response.data);
          return Promise.reject(error.response);
        }
        else if (error.response.status === 401) {
          APP_UTILITIES.removeCookies();
          window.location.href =
            location.href &&
            location.href.split('#')[0] + '#/login?type=logout';
        }
        axiosInstance.interceptors.response.eject(interceptor);
      }
    );
  }

  createRequestInterceptor(httpLMSInstance);
  createAxiosResponseInterceptor(httpLMSInstance);
  return httpLMSInstance;
};

const httpEnrollmentClient = ({
  interceptor = true,
}: {
  interceptor?: boolean;
} = {}) => {
  const httpEnrollmentInstance = axios.create({
    baseURL: BASE_URLS.VUE_APP_BASE_ENROLLMENT_URL,
    headers: {
      'Access-Control-Allow-Origin': '*',
    },
  });

  function createAxiosResponseInterceptor(axiosInstance: any) {
    const interceptor = axiosInstance.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        if (error.response.status !== 401 && error.response.status !== 500) {
          Vue.prototype.$eventHub.$emit('showErrorPopup', error.response.data);
          return Promise.reject(error.response);
        }
        else if (error.response.status === 401) {
          if (UIkit && UIkit.modal('#modal-session-timeout')) {
            UIkit.modal('#modal-session-timeout').show();
          }
          else {
            APP_UTILITIES.removeCookies();
            window.location.href =
              location.href &&
              location.href.split('#')[0] + '#/login?type=logout';
          }
        }
        else if (error.response.status === 500) {
          const data = {
            name: 'Common_Error',
          };
          Vue.prototype.$eventHub.$emit('showErrorPopup', data);
        }
        axiosInstance.interceptors.response.eject(interceptor);
      }
    );
  }
  interceptor && createRequestInterceptor(httpEnrollmentInstance);
  createAxiosResponseInterceptor(httpEnrollmentInstance);
  return httpEnrollmentInstance;
};
export {
  httpClient,
  httpAdmin,
  httpLMSClient,
  httpIdentityClient,
  httpEnrollmentClient,
};
